import type { Bounds } from "./selectionBounds";
import type { ResizeHandleId } from "./transform/resize";
import type { SceneObject } from "./SceneHost";
import { isPathObject } from "./pathGeometry";
import { isSegmentObject } from "./segmentGeometry";
import { GRID_BASE_STEP_PX } from "../lib/board/boardViewport";

/** Порог прилипания в экранных пикселях (переводится в world через zoom). */
export const SMART_GUIDE_SNAP_PX = 14;
/** Минимальный размер стороны кандидата — иначе линии/штрихи «воруют» snap (верх≈низ). */
const MIN_ALIGN_SIDE_PX = 24;
/** Запас вокруг viewport (world px). */
const VIEWPORT_MARGIN_PX = 24;

export type AlignmentGuideX = { x: number; y1: number; y2: number; equalSize?: boolean };
export type AlignmentGuideY = { y: number; x1: number; x2: number; equalSize?: boolean };

export type SpacingGuide = {
  axis: "x" | "y";
  distance: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  labelX: number;
  labelY: number;
};

export type SnapResult = {
  dx: number;
  dy: number;
  guidesX: AlignmentGuideX[];
  guidesY: AlignmentGuideY[];
  guideX?: AlignmentGuideX;
  guideY?: AlignmentGuideY;
};

export function shiftedBounds(b: Bounds, dx: number, dy: number): Bounds {
  return { x: b.x + dx, y: b.y + dy, w: b.w, h: b.h };
}

function edgeValues(b: Bounds): { xs: number[]; ys: number[] } {
  return {
    xs: [b.x, b.x + b.w / 2, b.x + b.w],
    ys: [b.y, b.y + b.h / 2, b.y + b.h],
  };
}

/**
 * К чему можно прилипать: фигуры, картинки, PDF, стикеры, текст, виджеты.
 * Не кисть, не линии/стрелки и не крошечные объекты (у них «верх≈низ»).
 */
export function isAlignSnapTarget(obj: SceneObject): boolean {
  if (isPathObject(obj) || isSegmentObject(obj)) return false;
  const w = typeof obj.w === "number" ? obj.w : 0;
  const h = typeof obj.h === "number" ? obj.h : 0;
  return w >= MIN_ALIGN_SIDE_PX && h >= MIN_ALIGN_SIDE_PX;
}

function rangesOverlap(a1: number, a2: number, b1: number, b2: number): boolean {
  return Math.max(a1, b1) < Math.min(a2, b2);
}

function boundsIntersect(a: Bounds, b: Bounds): boolean {
  return (
    rangesOverlap(a.x, a.x + a.w, b.x, b.x + b.w) &&
    rangesOverlap(a.y, a.y + a.h, b.y, b.y + b.h)
  );
}

function centerDist2(a: Bounds, b: Bounds): number {
  const dx = a.x + a.w / 2 - (b.x + b.w / 2);
  const dy = a.y + a.h / 2 - (b.y + b.h / 2);
  return dx * dx + dy * dy;
}

function areaOf(b: Bounds): number {
  return Math.max(0, b.w) * Math.max(0, b.h);
}

/**
 * Лучший snap: меньше расстояние; при почти равном — более крупный объект, затем ближе по центру.
 */
function isBetterSnap(
  dist: number,
  bestDist: number,
  moving: Bounds,
  candidate: Bounds,
  bestCandidate: Bounds | null
): boolean {
  if (dist < bestDist - 0.25) return true;
  if (dist > bestDist + 0.25) return false;
  if (!bestCandidate) return true;
  const area = areaOf(candidate);
  const bestArea = areaOf(bestCandidate);
  if (area !== bestArea) return area > bestArea;
  return centerDist2(moving, candidate) < centerDist2(moving, bestCandidate);
}

/** Все совпавшие линии по оси Y после snap (у одинаковых объектов — верх, середина и низ). */
function collectAlignedGuidesY(final: Bounds, target: Bounds): AlignmentGuideY[] {
  const lines = new Set<number>();
  for (const my of edgeValues(final).ys) {
    for (const cy of edgeValues(target).ys) {
      if (Math.abs(my - cy) < 0.5) lines.add(cy);
    }
  }
  return [...lines].map((y) => guideBetweenY(final, target, y));
}

/** Все совпавшие линии по оси X после snap. */
function collectAlignedGuidesX(final: Bounds, target: Bounds): AlignmentGuideX[] {
  const lines = new Set<number>();
  for (const mx of edgeValues(final).xs) {
    for (const cx of edgeValues(target).xs) {
      if (Math.abs(mx - cx) < 0.5) lines.add(cx);
    }
  }
  return [...lines].map((x) => guideBetweenX(final, target, x));
}

function normalizeGuideList<T>(guides?: T | T[] | null): T[] {
  if (!guides) return [];
  return Array.isArray(guides) ? guides : [guides];
}

/** Screen px → world px с учётом текущего зума. */
export function snapThresholdWorld(
  stagePoint: (clientX: number, clientY: number) => { x: number; y: number },
  screenPx = SMART_GUIDE_SNAP_PX
): number {
  const a = stagePoint(0, 0);
  const b = stagePoint(screenPx, 0);
  const w = Math.abs(b.x - a.x);
  return Number.isFinite(w) && w > 1e-6 ? w : screenPx;
}

/**
 * Видимая область доски в world-координатах.
 */
export function visibleWorldBoundsFromViewport(
  stagePoint: (clientX: number, clientY: number) => { x: number; y: number },
  viewportEl: Element
): Bounds {
  const rect = viewportEl.getBoundingClientRect();
  const tl = stagePoint(rect.left, rect.top);
  const br = stagePoint(rect.right, rect.bottom);
  const x1 = Math.min(tl.x, br.x) - VIEWPORT_MARGIN_PX;
  const y1 = Math.min(tl.y, br.y) - VIEWPORT_MARGIN_PX;
  const x2 = Math.max(tl.x, br.x) + VIEWPORT_MARGIN_PX;
  const y2 = Math.max(tl.y, br.y) + VIEWPORT_MARGIN_PX;
  return { x: x1, y: y1, w: Math.max(1, x2 - x1), h: Math.max(1, y2 - y1) };
}

/** Кандидаты, пересекающие viewport (legacy). */
export function filterVisibleCandidates(candidates: Bounds[], view: Bounds): Bounds[] {
  return candidates.filter((c) => boundsIntersect(c, view));
}

/** Объект «на экране» — центр bbox внутри видимой области. */
export function isBoundsVisibleOnScreen(bounds: Bounds, view: Bounds): boolean {
  if (view.w < 8 || view.h < 8) return false;
  const cx = bounds.x + bounds.w / 2;
  const cy = bounds.y + bounds.h / 2;
  return cx >= view.x && cx <= view.x + view.w && cy >= view.y && cy <= view.y + view.h;
}

function isWorldCoordVisibleOnScreen(coord: number, axis: "x" | "y", view: Bounds): boolean {
  if (view.w < 8 || view.h < 8) return false;
  if (axis === "x") return coord >= view.x && coord <= view.x + view.w;
  return coord >= view.y && coord <= view.y + view.h;
}

function resolveNearby(candidates: Bounds[], view?: Bounds): Bounds[] {
  if (!view || view.w < 8 || view.h < 8) return [];
  return candidates.filter((c) => isBoundsVisibleOnScreen(c, view));
}

function guideBetweenX(moving: Bounds, target: Bounds, x: number): AlignmentGuideX {
  // Только зазор между объектами — иначе линия через PDF выглядит как snap к содержимому.
  let y1: number;
  let y2: number;
  if (moving.y + moving.h < target.y) {
    y1 = moving.y + moving.h;
    y2 = target.y;
  } else if (target.y + target.h < moving.y) {
    y1 = target.y + target.h;
    y2 = moving.y;
  } else {
    y1 = Math.min(moving.y, target.y);
    y2 = Math.max(moving.y + moving.h, target.y + target.h);
  }
  return { x, y1, y2 };
}

function guideBetweenY(moving: Bounds, target: Bounds, y: number): AlignmentGuideY {
  let x1: number;
  let x2: number;
  if (moving.x + moving.w < target.x) {
    x1 = moving.x + moving.w;
    x2 = target.x;
  } else if (target.x + target.w < moving.x) {
    x1 = target.x + target.w;
    x2 = moving.x;
  } else {
    x1 = Math.min(moving.x, target.x);
    x2 = Math.max(moving.x + moving.w, target.x + target.w);
  }
  return { y, x1, x2 };
}

/**
 * Прилипание к краям/центрам объектов, видимых на экране.
 * Все 3×3 комбинации: верх/середина/низ × верх/середина/низ (и то же по X).
 * Одна пара dx/dy — к лучшему кандидату; линий может быть несколько (одинаковые объекты).
 */
export function snapBoundsToObjects(
  moving: Bounds,
  candidates: Bounds[],
  threshold: number,
  view?: Bounds
): SnapResult {
  const nearby = resolveNearby(candidates, view).filter(
    (c) => c.w >= MIN_ALIGN_SIDE_PX && c.h >= MIN_ALIGN_SIDE_PX
  );
  const { xs: movingXs, ys: movingYs } = edgeValues(moving);

  let bestDx = 0;
  let bestDy = 0;
  let bestXDist = threshold + 1;
  let bestYDist = threshold + 1;
  let winX: Bounds | null = null;
  let winY: Bounds | null = null;

  for (const candidate of nearby) {
    const { xs: candidateXs, ys: candidateYs } = edgeValues(candidate);

    for (let mi = 0; mi < movingXs.length; mi++) {
      const mx = movingXs[mi]!;
      for (let ci = 0; ci < candidateXs.length; ci++) {
        const cx = candidateXs[ci]!;
        const diff = cx - mx;
        const dist = Math.abs(diff);
        if (dist > threshold) continue;
        if (view && !isWorldCoordVisibleOnScreen(cx, "x", view)) continue;
        if (view && !isWorldCoordVisibleOnScreen(mx, "x", view)) continue;
        if (isBetterSnap(dist, bestXDist, moving, candidate, winX)) {
          bestXDist = dist;
          bestDx = diff;
          winX = candidate;
        }
      }
    }

    for (let mi = 0; mi < movingYs.length; mi++) {
      const my = movingYs[mi]!;
      for (let ci = 0; ci < candidateYs.length; ci++) {
        const cy = candidateYs[ci]!;
        const diff = cy - my;
        const dist = Math.abs(diff);
        if (dist > threshold) continue;
        if (view && !isWorldCoordVisibleOnScreen(cy, "y", view)) continue;
        if (view && !isWorldCoordVisibleOnScreen(my, "y", view)) continue;
        if (isBetterSnap(dist, bestYDist, moving, candidate, winY)) {
          bestYDist = dist;
          bestDy = diff;
          winY = candidate;
        }
      }
    }
  }

  const finalBounds = shiftedBounds(moving, bestDx, bestDy);
  const guidesX = winX ? collectAlignedGuidesX(finalBounds, winX) : [];
  const guidesY = winY ? collectAlignedGuidesY(finalBounds, winY) : [];

  return {
    dx: bestDx,
    dy: bestDy,
    guidesX,
    guidesY,
    guideX: guidesX[0],
    guideY: guidesY[0],
  };
}

/**
 * Направляющие без изменения позиции — только подсказка при близком выравнивании.
 */
export function findAlignmentGuides(
  moving: Bounds,
  candidates: Bounds[],
  threshold: number,
  view?: Bounds
): { guidesX: AlignmentGuideX[]; guidesY: AlignmentGuideY[] } {
  if (!view || view.w < 8 || view.h < 8) {
    return { guidesX: [], guidesY: [] };
  }

  const nearby = resolveNearby(candidates, view).filter(
    (c) => c.w >= MIN_ALIGN_SIDE_PX && c.h >= MIN_ALIGN_SIDE_PX
  );
  const { xs: movingXs, ys: movingYs } = edgeValues(moving);
  const guidesXMap = new Map<number, AlignmentGuideX>();
  const guidesYMap = new Map<number, AlignmentGuideY>();

  for (const candidate of nearby) {
    const { xs: candidateXs, ys: candidateYs } = edgeValues(candidate);

    for (const mx of movingXs) {
      for (const cx of candidateXs) {
        if (Math.abs(cx - mx) > threshold) continue;
        if (!isWorldCoordVisibleOnScreen(cx, "x", view)) continue;
        if (!isWorldCoordVisibleOnScreen(mx, "x", view)) continue;
        if (!guidesXMap.has(cx)) {
          guidesXMap.set(cx, guideBetweenX(moving, candidate, cx));
        }
      }
    }

    for (const my of movingYs) {
      for (const cy of candidateYs) {
        if (Math.abs(cy - my) > threshold) continue;
        if (!isWorldCoordVisibleOnScreen(cy, "y", view)) continue;
        if (!isWorldCoordVisibleOnScreen(my, "y", view)) continue;
        if (!guidesYMap.has(cy)) {
          guidesYMap.set(cy, guideBetweenY(moving, candidate, cy));
        }
      }
    }
  }

  return {
    guidesX: [...guidesXMap.values()],
    guidesY: [...guidesYMap.values()],
  };
}

/** Прилипание позиции к сетке (Alt при перемещении). Без пунктиров. */
export function snapBoundsToGrid(
  bounds: Bounds,
  gridStep = GRID_BASE_STEP_PX
): SnapResult {
  const snapX = Math.round(bounds.x / gridStep) * gridStep;
  const snapY = Math.round(bounds.y / gridStep) * gridStep;
  return {
    dx: snapX - bounds.x,
    dy: snapY - bounds.y,
    guidesX: [],
    guidesY: [],
  };
}

export type ResizeSnapResult = {
  bounds: Bounds;
  guidesX: AlignmentGuideX[];
  guidesY: AlignmentGuideY[];
  guideX?: AlignmentGuideX;
  guideY?: AlignmentGuideY;
};

/** Прилипание активных сторон bbox при resize. */
export function snapResizeBounds(
  bounds: Bounds,
  handle: ResizeHandleId,
  candidates: Bounds[],
  threshold: number,
  view?: Bounds
): ResizeSnapResult {
  let { x, y, w, h } = bounds;
  const nearby = resolveNearby(candidates, view).filter(
    (c) => c.w >= MIN_ALIGN_SIDE_PX && c.h >= MIN_ALIGN_SIDE_PX
  );
  const moving = { x, y, w, h };

  let snapXLine: number | undefined;
  let snapYLine: number | undefined;
  let winX: Bounds | null = null;
  let winY: Bounds | null = null;
  let bestXDist = threshold + 1;
  let bestYDist = threshold + 1;

  for (const candidate of nearby) {
    const cXs = [candidate.x, candidate.x + candidate.w / 2, candidate.x + candidate.w];
    const cYs = [candidate.y, candidate.y + candidate.h / 2, candidate.y + candidate.h];

    if (handle.includes("w") || handle.includes("e")) {
      const edge = handle.includes("w") ? x : x + w;
      for (const cx of cXs) {
        const dist = Math.abs(cx - edge);
        if (dist > threshold) continue;
        if (view && !isWorldCoordVisibleOnScreen(cx, "x", view)) continue;
        if (view && !isWorldCoordVisibleOnScreen(edge, "x", view)) continue;
        if (isBetterSnap(dist, bestXDist, moving, candidate, winX)) {
          bestXDist = dist;
          snapXLine = cx;
          winX = candidate;
        }
      }
    }
    if (handle.includes("n") || handle.includes("s")) {
      const edge = handle.includes("n") ? y : y + h;
      for (const cy of cYs) {
        const dist = Math.abs(cy - edge);
        if (dist > threshold) continue;
        if (view && !isWorldCoordVisibleOnScreen(cy, "y", view)) continue;
        if (view && !isWorldCoordVisibleOnScreen(edge, "y", view)) continue;
        if (isBetterSnap(dist, bestYDist, moving, candidate, winY)) {
          bestYDist = dist;
          snapYLine = cy;
          winY = candidate;
        }
      }
    }
  }

  if (winX && snapXLine !== undefined) {
    if (handle.includes("w")) {
      w += x - snapXLine;
      x = snapXLine;
    } else if (handle.includes("e")) {
      w = snapXLine - x;
    }
  }
  if (winY && snapYLine !== undefined) {
    if (handle.includes("n")) {
      h += y - snapYLine;
      y = snapYLine;
    } else if (handle.includes("s")) {
      h = snapYLine - y;
    }
  }

  const next = { x, y, w: Math.max(16, w), h: Math.max(16, h) };
  const guidesX = winX ? collectAlignedGuidesX(next, winX) : [];
  const guidesY = winY ? collectAlignedGuidesY(next, winY) : [];

  return {
    bounds: next,
    guidesX,
    guidesY,
    guideX: guidesX[0],
    guideY: guidesY[0],
  };
}

/** Оверлей направляющих и расстояний. */
export class SmartGuideOverlay {
  private readonly root: HTMLElement;
  private alignmentEls: HTMLElement[] = [];
  private spacingEls: HTMLElement[] = [];

  constructor(parent: HTMLElement) {
    this.root = document.createElement("div");
    this.root.className = "wb-smart-guides-layer";
    parent.appendChild(this.root);
  }

  renderAlignment(
    guidesX?: AlignmentGuideX | AlignmentGuideX[] | null,
    guidesY?: AlignmentGuideY | AlignmentGuideY[] | null
  ) {
    const linesX = normalizeGuideList(guidesX);
    const linesY = normalizeGuideList(guidesY);
    const needed = linesX.length + linesY.length;

    while (this.alignmentEls.length > needed) {
      this.alignmentEls.pop()?.remove();
    }
    while (this.alignmentEls.length < needed) {
      const el = document.createElement("div");
      this.root.appendChild(el);
      this.alignmentEls.push(el);
    }

    let i = 0;
    for (const g of linesX) {
      const el = this.alignmentEls[i++]!;
      el.className = "wb-smart-guide wb-smart-guide--x";
      el.style.left = `${g.x}px`;
      el.style.top = `${Math.min(g.y1, g.y2)}px`;
      el.style.width = "";
      el.style.height = `${Math.max(1, Math.abs(g.y2 - g.y1))}px`;
    }
    for (const g of linesY) {
      const el = this.alignmentEls[i++]!;
      el.className = "wb-smart-guide wb-smart-guide--y";
      el.style.left = `${Math.min(g.x1, g.x2)}px`;
      el.style.top = `${g.y}px`;
      el.style.width = `${Math.max(1, Math.abs(g.x2 - g.x1))}px`;
      el.style.height = "";
    }
  }

  renderSpacing(guides: SpacingGuide[]) {
    this.clearSpacing();
    for (const g of guides) {
      const line = document.createElement("div");
      line.className = `wb-spacing-guide wb-spacing-guide--${g.axis}`;
      if (g.axis === "x") {
        line.style.left = `${Math.min(g.x1, g.x2)}px`;
        line.style.top = `${g.y1}px`;
        line.style.width = `${Math.max(1, Math.abs(g.x2 - g.x1))}px`;
        line.style.height = "1px";
      } else {
        line.style.left = `${g.x1}px`;
        line.style.top = `${Math.min(g.y1, g.y2)}px`;
        line.style.width = "1px";
        line.style.height = `${Math.max(1, Math.abs(g.y2 - g.y1))}px`;
      }
      this.root.appendChild(line);
      this.spacingEls.push(line);

      const cap1 = document.createElement("div");
      cap1.className = "wb-spacing-guide__cap";
      cap1.style.left = `${g.x1}px`;
      cap1.style.top = `${g.y1}px`;
      this.root.appendChild(cap1);
      this.spacingEls.push(cap1);

      const cap2 = document.createElement("div");
      cap2.className = "wb-spacing-guide__cap";
      cap2.style.left = `${g.x2}px`;
      cap2.style.top = `${g.y2}px`;
      this.root.appendChild(cap2);
      this.spacingEls.push(cap2);

      const label = document.createElement("div");
      label.className = "wb-spacing-guide__label";
      label.textContent = String(g.distance);
      label.style.left = `${g.labelX}px`;
      label.style.top = `${g.labelY}px`;
      this.root.appendChild(label);
      this.spacingEls.push(label);
    }
  }

  private clearAlignment() {
    for (const el of this.alignmentEls) el.remove();
    this.alignmentEls = [];
  }

  clearSpacing() {
    for (const el of this.spacingEls) el.remove();
    this.spacingEls = [];
  }

  clear() {
    this.clearAlignment();
    this.clearSpacing();
  }

  destroy() {
    this.clear();
    this.root.remove();
  }
}
